import { useEffect, useState } from "react";
import BrightcoveVideo from "@atoms/players/BrightcoveVideo";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import Header from "@components/Header/Header";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import Taboola from "@features/taboola/Taboola";
import { faClock } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { VideosDetailsPageData } from "@routes/Videos/VideosDetailsPage/utils/types";
import { ENVIRONMENT } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import dayjs from "dayjs";

import RelatedVideos from "./components/RelatedVideos";

/**
 * This is the component for displaying the videos details page content.
 */
const VideosDetailsPageContent = ({
  video,
  relatedVideos,
  seoFooterData,
}: VideosDetailsPageData): React.ReactElement => {
  const [dateTime, setDateTime] = useState<string>("");

  const { title, videoUrl, publishedAt, id, description } = video;
  const envDomain = getEnvironmentDomain(ENVIRONMENT);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const formattedDate = dayjs(publishedAt).format(
      "ddd, MMMM DD, YYYY - hh:mm A"
    );
    setDateTime(formattedDate);
  }, [publishedAt]);

  return (
    <div className="w-full" data-testid="videos-details-page-content-component">
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header />

      <PulseEntry />

      <Container rootClassName="pt-6">
        <h1 className="mb-2 font-lct text-8xl">{title}</h1>

        <div className="mb-3 flex items-center gap-1 border-y border-gray-175 py-2">
          <FontAwesomeIcon
            icon={faClock}
            className="text-gray-675"
            size="xs"
            height={12}
            width={12}
          />

          <p className="mb-0 font-poppins text-3xs text-gray-675">{dateTime}</p>
        </div>

        <BrightcoveVideo videoId={id} className="mb-6 w-full" />

        <p className="mb-6 font-poppins text-base text-gray-850">
          {description}
        </p>

        <Ad
          adType="imu1"
          className="flex flex-col items-center justify-center"
        />
      </Container>

      <div className="my-6 bg-gray-250 py-4">
        <Container>
          <RelatedVideos videos={relatedVideos} />
        </Container>
      </div>

      <Container rootClassName="pb-6">
        <Taboola
          container="below-video-thumbnails"
          placement="Below Video Thumbnails"
          url={`${envDomain}${videoUrl}`}
          pageType="video"
          isFlush
        />
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default VideosDetailsPageContent;
